
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogClose,
  DialogFooter,
} from "@/components/ui/dialog";
import { Users, UserPlus, Mail, Trash, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/lib/toast";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { WorkflowMember } from "@/types/team";

interface WorkflowMembersDialogProps {
  boardId: string;
  boardName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface PendingInvite {
  id: string;
  email: string;
  created_at: string;
}

export function WorkflowMembersDialog({ boardId, boardName, open, onOpenChange }: WorkflowMembersDialogProps) {
  const [email, setEmail] = useState("");
  const [members, setMembers] = useState<WorkflowMember[]>([]);
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  
  const loadMembers = async () => {
    try {
      setIsLoading(true);
      
      console.log("Loading members for board:", boardId);
      
      // Load existing workflow members
      const { data: workflowMembers, error: membersError } = await supabase
        .from('workflow_members')
        .select(`
          id,
          user_id,
          created_at
        `)
        .eq('board_id', boardId);
        
      if (membersError) {
        console.error("Error loading workflow members:", membersError);
        throw membersError;
      }
      
      console.log("Workflow members found:", workflowMembers);
      
      // Fetch user details for each member
      const membersWithDetails = [];
      for (const member of workflowMembers || []) {
        try {
          const { data: userData, error: userError } = await supabase
            .from('users')
            .select('name, email')
            .eq('id', member.user_id)
            .maybeSingle();
            
          if (userError) {
            console.error("Error fetching user details for", member.user_id, ":", userError);
            // Continue without this user's details
            membersWithDetails.push({
              ...member,
              name: "Usuário",
              email: ""
            });
          } else if (userData) {
            membersWithDetails.push({ 
              ...member, 
              name: userData.name || "Usuário",
              email: userData.email || ""
            });
          }
        } catch (error) {
          console.error("Error processing member:", error);
          // Continue without this user's details
          membersWithDetails.push({
            ...member,
            name: "Usuário",
            email: ""
          });
        }
      }
      
      console.log("Members with details:", membersWithDetails);
      setMembers(membersWithDetails);
      
      // Load pending invites for this board
      const { data: invites, error: invitesError } = await supabase
        .from('team_invites')
        .select('id, email, created_at')
        .eq('board_id', boardId)
        .eq('accepted', false);
        
      if (invitesError) {
        console.error("Error loading invites:", invitesError);
        // Don't throw here, just set empty array
        setPendingInvites([]);
      } else {
        console.log("Pending invites:", invites);
        setPendingInvites(invites || []);
      }
    } catch (error) {
      console.error("Error loading workflow members:", error);
      toast.error("Erro ao carregar membros do fluxo de trabalho");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadMembers();
    }
  }, [open, boardId]);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSendInvite = async () => {
    if (isSending) return;
    
    const trimmedEmail = email.trim().toLowerCase();
    
    if (!trimmedEmail) {
      toast.error("Por favor, informe um email");
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      toast.error("Por favor, informe um email válido");
      return;
    }

    try {
      setIsSending(true);
      console.log("Starting invite process for:", trimmedEmail);

      // Get current user info
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        console.error("Auth error:", userError);
        toast.error("Erro de autenticação. Faça login novamente.");
        return;
      }

      console.log("Current user authenticated:", user.id);

      // Check if user already exists as a member
      const existingMember = members.find(m => m.email?.toLowerCase() === trimmedEmail);
      if (existingMember) {
        toast.error("Este usuário já é membro do quadro");
        return;
      }

      // Check if there's already a pending invite
      const existingInvite = pendingInvites.find(i => i.email?.toLowerCase() === trimmedEmail);
      if (existingInvite) {
        toast.error("Já existe um convite pendente para este email");
        return;
      }

      // Check if user exists in the system
      console.log("Checking if user exists:", trimmedEmail);
      const { data: existingUser, error: userCheckError } = await supabase
        .from('users')
        .select('id, name, email')
        .eq('email', trimmedEmail)
        .maybeSingle();

      if (userCheckError) {
        console.error("Error checking existing user:", userCheckError);
        // Continue with invitation flow even if check fails
      }

      console.log("User check result:", existingUser);

      if (existingUser) {
        // User exists - add directly to workflow_members
        console.log("User found, adding directly to workflow:", existingUser);
        
        // Double check if already a member (safety check)
        const { data: existingMemberCheck, error: memberCheckError } = await supabase
          .from('workflow_members')
          .select('id')
          .eq('user_id', existingUser.id)
          .eq('board_id', boardId)
          .maybeSingle();

        if (memberCheckError) {
          console.error("Error checking existing member:", memberCheckError);
        }

        if (existingMemberCheck) {
          toast.error("Este usuário já é membro do quadro");
          return;
        }

        // Add to workflow_members
        const { error: insertError } = await supabase
          .from('workflow_members')
          .insert({
            user_id: existingUser.id,
            board_id: boardId,
            created_by: user.id
          });

        if (insertError) {
          console.error("Error adding user to workflow:", insertError);
          toast.error("Erro ao adicionar usuário ao quadro: " + insertError.message);
          return;
        }

        // Create notification for the user
        try {
          await supabase
            .from('notifications')
            .insert({
              user_id: existingUser.id,
              type: 'workflow_invite',
              title: 'Adicionado ao quadro de trabalho',
              message: `Você foi adicionado ao quadro "${boardName || 'Quadro de trabalho'}"`,
              data: {
                board_id: boardId,
                board_name: boardName,
                inviter_id: user.id
              }
            });
        } catch (notificationError) {
          console.error("Error creating notification:", notificationError);
          // Don't fail the whole operation for notification error
        }

        toast.success(`${existingUser.name || existingUser.email} foi adicionado ao quadro`);
      } else {
        // User doesn't exist - send email invitation
        console.log("User not found, creating email invitation");
        
        // Get current user details using the new security definer function
        const { data: currentUserData, error: currentUserError } = await supabase
          .rpc('get_current_user_data');

        if (currentUserError) {
          console.error("Error fetching current user details:", currentUserError);
        }

        // Extract user data from the function result
        const currentUser = currentUserData && currentUserData.length > 0 ? {
          name: currentUserData[0].user_name,
          agency_name: currentUserData[0].user_agency_name
        } : { name: null, agency_name: null };

        // Generate a unique token for this invite
        const token = crypto.randomUUID();
        
        // Create invitation record with better error handling
        const { data: inviteData, error: inviteError } = await supabase
          .from('team_invites')
          .insert({
            inviter_id: user.id,
            email: trimmedEmail,
            role: 'member',
            permissions: {
              posts: false,
              approvals: false,
              workflow: [boardId],
              calendar: false,
              reports: false,
              automation: false,
              finances: false,
              clients: []
            },
            board_id: boardId,
            token: token
          })
          .select()
          .single();
        
        if (inviteError) {
          console.error("Error creating invite:", inviteError);
          toast.error("Erro ao criar convite: " + inviteError.message);
          return;
        }
        
        console.log("Invite created successfully:", inviteData);
        
        // Send invitation email via edge function
        const inviterName = currentUser.name || 'Um usuário';
        const companyName = currentUser.agency_name || 'Uma empresa';
        
        try {
          const { data: functionData, error: functionError } = await supabase.functions.invoke('send-invite', {
            body: {
              email: trimmedEmail,
              inviterName: inviterName,
              companyName: companyName,
              token: token,
              role: 'membro',
              boardInfo: boardName || 'Quadro de trabalho'
            }
          });
          
          if (functionError) {
            console.error("Function error:", functionError);
            toast.success("Convite criado com sucesso (email pode falhar)");
          } else {
            console.log("Email sent successfully:", functionData);
            toast.success(`Convite enviado para ${trimmedEmail}`);
          }
        } catch (functionError) {
          console.error("Error calling function:", functionError);
          toast.success("Convite criado com sucesso (email pode falhar)");
        }
      }
      
      setEmail("");
      loadMembers();
    } catch (error) {
      console.error("Error in invite process:", error);
      toast.error("Erro ao processar convite: " + (error instanceof Error ? error.message : 'Erro desconhecido'));
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isSending) {
      handleSendInvite();
    }
  };
  
  const handleCancelInvite = async (inviteId: string) => {
    try {
      const { error } = await supabase
        .from('team_invites')
        .delete()
        .eq('id', inviteId);
        
      if (error) throw error;
      
      setPendingInvites(pendingInvites.filter(invite => invite.id !== inviteId));
      toast.success("Convite cancelado com sucesso");
    } catch (error) {
      console.error("Error canceling invitation:", error);
      toast.error("Erro ao cancelar convite");
    }
  };
  
  const handleRemoveMember = async (memberId: string) => {
    try {
      const { error } = await supabase
        .from('workflow_members')
        .delete()
        .eq('id', memberId);
        
      if (error) throw error;
      
      setMembers(members.filter(member => member.id !== memberId));
      toast.success("Membro removido com sucesso");
    } catch (error) {
      console.error("Error removing workflow member:", error);
      toast.error("Erro ao remover membro");
    }
  };
  
  const getInitials = (name?: string) => {
    if (!name) return "?";
    return name
      .split(' ')
      .slice(0, 2)
      .map(word => word[0])
      .join('')
      .toUpperCase();
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>Gerenciar Membros</DialogTitle>
          <DialogDescription>
            Adicione ou remova membros deste quadro de trabalho.
          </DialogDescription>
        </DialogHeader>
        
        <div className="mt-4 space-y-4">
          <div>
            <h3 className="text-sm font-medium mb-2">Convidar membro</h3>
            <div className="flex space-x-2">
              <Input
                type="email"
                placeholder="Digite o email para convidar..."
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={handleKeyPress}
                className="flex-1"
                disabled={isSending}
              />
              <Button 
                onClick={handleSendInvite}
                disabled={isSending || !email.trim()}
                className="shrink-0"
              >
                <Send className="h-4 w-4 mr-2" />
                {isSending ? "Enviando..." : "Enviar"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Se o email existir no sistema, o usuário será adicionado diretamente. Caso contrário, será enviado um convite por email.
            </p>
          </div>
          
          <div>
            <h3 className="text-sm font-medium mb-2">Membros atuais</h3>
            {isLoading ? (
              <div className="text-center py-6">
                <p className="text-sm text-muted-foreground">Carregando...</p>
              </div>
            ) : members.length > 0 ? (
              <div className="border rounded-md overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Usuário</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {members.map((member) => (
                      <TableRow key={member.id}>
                        <TableCell>
                          <div className="flex items-center space-x-3">
                            <Avatar className="h-8 w-8">
                              <AvatarFallback>{getInitials(member.name)}</AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium">{member.name || "Usuário"}</p>
                              <p className="text-xs text-muted-foreground">{member.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveMember(member.id)}
                          >
                            <Trash className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-6 border rounded-md">
                <p className="text-sm text-muted-foreground">Nenhum membro adicionado</p>
              </div>
            )}
          </div>
          
          {pendingInvites.length > 0 && (
            <div>
              <h3 className="text-sm font-medium mb-2">Convites pendentes</h3>
              <div className="border rounded-md overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingInvites.map((invite) => (
                      <TableRow key={invite.id}>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <Mail className="h-4 w-4" />
                            <span>{invite.email}</span>
                            <Badge variant="secondary" className="ml-2">Pendente</Badge>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCancelInvite(invite.id)}
                          >
                            <Trash className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>
        
        <DialogFooter className="mt-4">
          <DialogClose asChild>
            <Button variant="outline">Fechar</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
